import { useRef, useState } from 'react';
import { Calendar } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { datumDe } from '@/lib/format';
import { useToast } from '@/lib/toast';

type Props = {
  value: string | null;
  onSave: (datum: string | null) => void;
};

// Ein Abholtermin liegt weder im Mittelalter noch in ferner Zukunft.
const JAHR_MIN = '2020-01-01';
const JAHR_MAX = '2100-12-31';

export function AbholdatumEditor({ value, onSave }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const feldRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  // Vier Faelle, die vorher alle mit "Gespeichert" quittiert wurden
  // (B75, A9):
  //
  //   leeres Feld, kein Datum gesetzt  ->  nichts zu tun, nur sagen warum
  //   leeres Feld, Datum war gesetzt   ->  bewusstes Entfernen
  //   halb getipptes Datum             ->  Eingabefehler, nichts anfassen
  //   Jahr vertippt (0912, 0026)       ->  Eingabefehler, nichts anfassen
  //
  // Der dritte Fall braucht validity.badInput: bei unvollstaendiger
  // Eingabe liefert das Feld denselben leeren Wert wie ein geraeumtes.
  // Ohne die Unterscheidung wuerde Tippen ein gesetztes Datum loeschen.
  //
  // Der vierte kam beim Nachmessen dazu: wer "05.09.26" tippt, landet im
  // Jahr 0026, und das Feld haelt das fuer ein gueltiges Datum. Die
  // Grenzen unten machen daraus einen Eingabefehler.
  function speichern(): void {
    const feld = feldRef.current;
    if (feld?.validity.badInput === true) {
      toast('error', 'Datum unvollständig.');
      return;
    }
    if (feld?.validity.rangeUnderflow === true || feld?.validity.rangeOverflow === true) {
      toast('error', 'Jahr sieht nicht richtig aus.');
      return;
    }
    const neu = draft === '' ? null : draft;
    if (neu === null && value === null) {
      toast('error', 'Kein Datum gewählt.');
      return;
    }
    if (neu === value) {
      setEditing(false);
      return;
    }
    onSave(neu);
    setEditing(false);
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(value ?? '');
          setEditing(true);
        }}
        className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-ink-soft hover:text-akzent"
        title="Zugesagter Abhol- bzw. Liefertermin"
      >
        <Calendar className="h-4 w-4" />
        Termin: {value === null ? '—' : datumDe(value)}
      </button>
    );
  }
  return (
    <div className="inline-flex flex-wrap items-center gap-2 text-sm">
      <Calendar className="h-4 w-4 text-ink-soft" />
      <input
        ref={feldRef}
        type="date"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        // Nicht "Abholdatum": das Feld traegt den zugesagten Tag, gleich
        // ob die Ware abgeholt oder versendet wird — genau wie die
        // Beschriftung "Termin:" davor und die Dashboard-Spalte. Der
        // Screenreader nannte hier als einziger noch das alte Wort (B110).
        aria-label="Termin vereinbart am"
        // Grosszuegig gesetzt: es geht nur darum, vertippte Jahreszahlen
        // abzufangen, nicht darum, Termine vorzuschreiben.
        min={JAHR_MIN}
        max={JAHR_MAX}
        className="min-h-[44px] rounded border border-line bg-cream px-2 py-1"
      />
      <Button variant="primary" onClick={speichern}>
        Speichern
      </Button>
      {value !== null && (
        <Button variant="ghost" onClick={() => { onSave(null); setEditing(false); }}>
          Datum entfernen
        </Button>
      )}
      <Button variant="ghost" onClick={() => setEditing(false)}>
        Abbrechen
      </Button>
    </div>
  );
}
